import { ArrowBack } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLoaderData, useNavigate, useRevalidator } from "react-router-dom";

import { inject } from "../../../../../lib/itn-ui-kit/services/service-container";
import { useHistoryNavigate } from "../../../../hooks/useHistoryNavigate";
import useService from "../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../global/ApiRequestErrorHandler";
import OverlayLoadingIndicator from "../../../global/Loading/OverlayLoadingIndicator";
import PermissionWrapper from "../../../global/PermissionWrapper";
import ResponseModal from "../../../global/ResponseModal";
import Empty from "../../../ui library/components/data-grid/Empty";
import HNSColors from "../../../ui library/HNSColors";
import UploadDocuments from "../UploadDocuments";

import DocumentsList from "./components/DocumentsList";
import FolderList from "./components/FolderList";
import { useDocumentListStore } from "./hooks/useDocumentListStore";

export default function ViewDocuments({ customBaseDetailPath }) {
  const currentUserService = inject("current-user-service");
  const exportAllRepository = useService("repositories/export-all-repository");
  const permissions = currentUserService?.user?.permissions;
  const canDownloadAllPermission = permissions.includes("casemanager.can_export_all_documents");
  const { revalidate } = useRevalidator();
  const { _case, folders, title } = useLoaderData();
  const { navigationHistoryService } = useHistoryNavigate();

  const {
    setFolderId,
    setLoading,
    isLoading,
    parentFolderId,
    isUploadModalOpen,
    setIsUploadModalOpen,
  } = useDocumentListStore();
  const navigate = useNavigate();
  const folderList = folders.folders;
  const documentList = folders.documents;
  const [currentFolder, setCurrentFolder] = useState("");
  const [downloadAll, setDownloadAll] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!isUploadModalOpen) {
      revalidate();
    }
  }, [isUploadModalOpen]);

  useEffect(() => {
    setLoading(false);
  }, [folders, setLoading]);

  useEffect(() => {
    setCurrentFolder(folders.folderName);
  }, [folders.folderName]);

  const handleBackArrow = () => {
    setLoading(true);
    setFolderId(parentFolderId);
    revalidate();
  };

  const onClickUpload = () => setIsUploadModalOpen(true);

  const onClickDownloadAll = async () => {
    try {
      const response = await exportAllRepository.export({ id: _case?.id });
      setSuccessMessage(response?.message);
      setDownloadAll(true);
    } catch (error) {
      setDownloadAll(false);
      setOpenError(true);
      const res = error;
      const errArr = ApiRequestErrorHandler(res);
      setErrorMessage(errArr);
    }
  };

  const onClickBackToClientDetail = () => {
    if (customBaseDetailPath) {
      navigate(
        `${customBaseDetailPath}${_case.id}${navigationHistoryService.get("client-detail")}`,
        { replace: true }
      );
    }
  };

  return (
    <>
      <div className="m-2 flex flex-col">
        {isLoading && <OverlayLoadingIndicator />}
        {folderList.length === 0 && documentList.length === 0 ? (
          <>{Empty}</>
        ) : (
          <>
            <div className="flex w-full flex-row-reverse justify-between">
              {customBaseDetailPath && (
                <Button
                  className="mx-2 bg-green-600 px-4 py-2 text-white hover:bg-green-100 hover:text-green-600"
                  onClick={onClickBackToClientDetail}
                >
                  Back to Client Detail
                </Button>
              )}
              <PermissionWrapper permission={canDownloadAllPermission}>
                <Button
                  className="mx-2 px-4 py-2 text-white"
                  onClick={onClickDownloadAll}
                  style={{
                    backgroundColor: HNSColors.primary[300],
                  }}
                >
                  Download All Documents
                </Button>
              </PermissionWrapper>
              <Button
                className="mx-2 bg-blue-600 px-4 py-2 text-white hover:bg-blue-100 hover:text-blue-600"
                onClick={onClickUpload}
              >
                Upload Document
              </Button>
              {title && (
                <Typography className="flex-1 text-center text-lg font-bold">{title}</Typography>
              )}
              {parentFolderId &&
                currentFolder &&
                (folderList.length > 0 || documentList.length > 0) && (
                  <div className="flex flex-row gap-1">
                    <ArrowBack className="hover:cursor-pointer" onClick={handleBackArrow} />
                    <Typography className="flex-1 text-center text-base font-bold">
                      {currentFolder}
                    </Typography>
                  </div>
                )}
            </div>
            {folderList.length > 0 && (
              <FolderList _case={_case} folders={folderList} setCurrentFolder={setCurrentFolder} />
            )}
            {documentList.length > 0 && <DocumentsList _case={_case} documents={documentList} />}
          </>
        )}
      </div>
      <UploadDocuments isModal={true} />
      <ResponseModal
        title="Documents Requested"
        description={successMessage}
        openBool={downloadAll}
        setOpenBool={setDownloadAll}
      />
      <ResponseModal
        title="Error"
        isError={true}
        openBool={openError}
        setOpenBool={setOpenError}
        errorMessage={errorMessage}
      />
    </>
  );
}
